import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
  ENTITY_BADGE_CLASSES,
  ENTITY_TYPE_LABELS,
} from "../../constants/entities";
import { INTERACTION_TYPE_LABELS } from "../../constants/interactions";
import type { Interaction } from "../../hooks/useSandboxPersonas";
import type { SandboxEntityType } from "../../types/entities";
import type { InteractionType } from "../../types/interactions";

interface InteractionContributionsProps {
  interactions: Interaction[];
  getWeight: (entityType: SandboxEntityType, interactionType: InteractionType) => number;
  getDecayMultiplier: (interaction: Interaction) => number;
}

interface ContributionRow {
  id: string;
  entityType: SandboxEntityType;
  interactionType: InteractionType;
  entityName: string;
  baseWeight: number;
  decayMultiplier: number;
  effectiveWeight: number;
  pct: number;
}

export function InteractionContributions({
  interactions,
  getWeight,
  getDecayMultiplier,
}: InteractionContributionsProps) {
  const rows: ContributionRow[] = interactions.map((i) => {
    const baseWeight = getWeight(i.entityType, i.interactionType);
    const decayMultiplier = getDecayMultiplier(i);
    const effectiveWeight = baseWeight * decayMultiplier;
    return {
      id: i.id,
      entityType: i.entityType,
      interactionType: i.interactionType,
      entityName: i.entity.name,
      baseWeight,
      decayMultiplier,
      effectiveWeight,
      pct: 0, // filled below
    };
  });

  const total = rows.reduce((s, r) => s + r.effectiveWeight, 0);
  for (const row of rows) {
    row.pct = total > 0 ? (row.effectiveWeight / total) * 100 : 0;
  }

  if (rows.length === 0) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Per-interaction weights</h3>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.id} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "shrink-0 text-xs capitalize",
                  ENTITY_BADGE_CLASSES[row.entityType],
                )}
              >
                {ENTITY_TYPE_LABELS[row.entityType]}
              </Badge>
              <span className="flex-1 truncate text-sm font-medium">
                {row.entityName}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{INTERACTION_TYPE_LABELS[row.interactionType]}</span>
              <span className="tabular-nums font-mono">
                {row.baseWeight.toFixed(2)}{" "}
                <span className="text-muted-foreground/60">×</span>{" "}
                {row.decayMultiplier.toFixed(2)}{" "}
                <span className="text-muted-foreground/60">=</span>{" "}
                <span className="text-foreground font-semibold">
                  {row.effectiveWeight.toFixed(3)}
                </span>
              </span>
            </div>
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className={cn(
                  "absolute h-full rounded-full transition-all",
                  {
                    "bg-violet-400": row.entityType === "artist",
                    "bg-sky-400": row.entityType === "genre",
                    "bg-amber-400": row.entityType === "venue",
                    "bg-emerald-400": row.entityType === "event",
                  },
                )}
                style={{ width: `${row.pct}%` }}
              />
            </div>
            <p className="text-right text-xs tabular-nums text-muted-foreground">
              {row.pct.toFixed(1)}% of weighted sum
            </p>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground pt-1">
        Effective weight = base weight × decay multiplier. The weighted sum is
        normalised to a unit vector to form the persona embedding.
      </p>
    </section>
  );
}
